import { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

export interface CustomField {
  name: string;
  value: string;
  type: 'text' | 'number' | 'date' | 'select' | 'textarea';
  options?: string[];
}

interface CustomFieldsEditorProps {
  activityType: string;
  customFields: CustomField[];
  onChange: (fields: CustomField[]) => void;
  readOnly?: boolean;
}

// Default fields for each activity type
const getDefaultFieldsForType = (type: string): CustomField[] => {
  switch (type?.toUpperCase()) {
    case 'TRAVEL':
      return [
        { name: 'Transportation Mode', value: '', type: 'select', options: ['Car', 'Plane', 'Train', 'Helicopter'] },
        { name: 'Flight Number', value: '', type: 'text' },
        { name: 'Departure Airport', value: '', type: 'text' },
        { name: 'Arrival Airport', value: '', type: 'text' },
        { name: 'Layovers', value: '', type: 'textarea' },
        { name: 'Accommodation', value: '', type: 'text' },
        { name: 'Ground Transportation', value: '', type: 'text' },
        { name: 'Security Escort Required', value: '', type: 'select', options: ['Yes', 'No'] },
      ];

    case 'DINING':
      return [
        { name: 'Restaurant Name', value: '', type: 'text' },
        { name: 'Reservation Time', value: '', type: 'text' },
        { name: 'Party Size', value: '', type: 'number' },
        { name: 'Private Room', value: '', type: 'select', options: ['Yes', 'No'] },
        { name: 'Dietary Restrictions', value: '', type: 'textarea' },
        { name: 'Security Advance Required', value: '', type: 'select', options: ['Yes', 'No'] },
        { name: 'Venue Security Contact', value: '', type: 'text' },
      ];

    case 'EVENT':
      return [
        { name: 'Event Type', value: '', type: 'select', options: ['Conference', 'Gala', 'Meeting', 'Speech', 'Other'] },
        { name: 'Expected Attendance', value: '', type: 'number' },
        { name: 'Media Present', value: '', type: 'select', options: ['Yes', 'No'] },
        { name: 'Public Access', value: '', type: 'select', options: ['Yes', 'No'] },
        { name: 'VIP Attendees', value: '', type: 'textarea' },
        { name: 'Entry/Exit Points', value: '', type: 'textarea' },
        { name: 'Emergency Procedures', value: '', type: 'textarea' },
      ];

    case 'MEETING':
      return [
        { name: 'Meeting Type', value: '', type: 'select', options: ['Board Meeting', 'Client Meeting', 'Internal Meeting', 'Other'] },
        { name: 'Attendees', value: '', type: 'textarea' },
        { name: 'Confidentiality Level', value: '', type: 'select', options: ['Public', 'Internal', 'Confidential', 'Restricted'] },
        { name: 'Recording Allowed', value: '', type: 'select', options: ['Yes', 'No'] },
      ];

    default:
      return [
        { name: 'Activity Details', value: '', type: 'textarea' },
        { name: 'Special Instructions', value: '', type: 'textarea' },
      ];
  }
};

const CustomFieldsEditor: React.FC<CustomFieldsEditorProps> = ({
  activityType,
  customFields,
  onChange,
  readOnly = false,
}) => {
  const [previousActivityType, setPreviousActivityType] = useState<string>(activityType);

  // When activity type changes, load new default fields
  useEffect(() => {
    if (activityType !== previousActivityType) {
      const defaultFields = getDefaultFieldsForType(activityType);
      onChange(defaultFields);
      setPreviousActivityType(activityType);
    }
  }, [activityType, previousActivityType, onChange]);

  // Initialize with default fields if empty - runs once on mount
  useEffect(() => {
    if (customFields.length === 0 && activityType) {
      const defaultFields = getDefaultFieldsForType(activityType);
      onChange(defaultFields);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addCustomField = () => {
    onChange([
      ...customFields,
      { name: '', value: '', type: 'text' },
    ]);
  };

  const removeCustomField = (index: number) => {
    onChange(customFields.filter((_, i) => i !== index));
  };

  const updateCustomField = (index: number, field: Partial<CustomField>) => {
    const updated = [...customFields];
    updated[index] = { ...updated[index], ...field };
    onChange(updated);
  };

  const getActivityTypeLabel = (type: string) => {
    const normalized = type?.toUpperCase() ?? 'OTHER';
    return normalized.charAt(0) + normalized.slice(1).toLowerCase();
  };

  if (readOnly) {
    return (
      <div className="space-y-4">
        {customFields.map((field, index) => (
          <div key={index} className="border-b border-secondary-200 dark:border-secondary-700 last:border-0 pb-3 last:pb-0">
            <div className="text-sm text-secondary-600 dark:text-secondary-400 mb-1">
              {field.name}
            </div>
            <div className="font-medium text-secondary-900 dark:text-white">
              {field.value ?? 'Not specified'}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-medium text-secondary-900 dark:text-white">
          {getActivityTypeLabel(activityType)} Details
        </h4>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addCustomField}
        >
          <PlusIcon className="w-4 h-4 mr-1" />
          Add Field
        </Button>
      </div>

      {customFields.length > 0 && (
        <div className="space-y-4 border rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50">
          {customFields.map((field, index) => (
            <div key={index} className="flex items-start gap-4">
              <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
                {/* Field Name Input */}
                <input
                  type="text"
                  value={field.name}
                  onChange={(e) => updateCustomField(index, { name: e.target.value })}
                  placeholder="Field name"
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 text-secondary-900 dark:text-white"
                />

                {/* Field Type Selector */}
                <select
                  value={field.type}
                  onChange={(e) => updateCustomField(index, { type: e.target.value as CustomField['type'] })}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 text-secondary-900 dark:text-white"
                >
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="date">Date</option>
                  <option value="select">Select</option>
                  <option value="textarea">Long Text</option>
                </select>

                {/* Dynamic Value Input */}
                {field.type === 'select' ? (
                  <select
                    value={field.value}
                    onChange={(e) => updateCustomField(index, { value: e.target.value })}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 text-secondary-900 dark:text-white"
                  >
                    <option value="">Select...</option>
                    {field.options?.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                ) : field.type === 'textarea' ? (
                  <textarea
                    value={field.value}
                    onChange={(e) => updateCustomField(index, { value: e.target.value })}
                    placeholder="Value"
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 text-secondary-900 dark:text-white"
                  />
                ) : (
                  <input
                    type={field.type}
                    value={field.value}
                    onChange={(e) => updateCustomField(index, { value: e.target.value })}
                    placeholder="Value"
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 text-secondary-900 dark:text-white"
                  />
                )}
              </div>

              {/* Delete Button */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => removeCustomField(index)}
              >
                <TrashIcon className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomFieldsEditor;
